import classNames from "classnames/bind";
import { Formik, FormikProps, FormikValues } from "formik";
import { useTranslation } from "next-i18next";
import React, { FocusEventHandler, useMemo, useState } from "react";
import * as Yup from "yup";

import { isValidEmailForSalesforce } from "@/utils/email-validation";

import ActionButton from "@/components/buttons/action-button";
import {
    KINDS as BUTTON_KINDS,
    SIZES as BUTTON_SIZES,
} from "@/components/buttons/buttons.constants";
import { DropdownMenu } from "@/components/dropdown-menus/dropdown-menu";
import { DropdownMenuItem } from "@/components/dropdown-menus/dropdown-menu-items/dropdown-menu-item-interface";
import {
    KINDS as DROPDOWN_KINDS,
    SIZES as DROPDOWN_SIZES,
} from "@/components/dropdown-menus/dropdown-menu.constants";
import { SIZES as INPUT_SIZES, TYPES } from "@/components/inputs/input.constants";
import { TextInput } from "@/components/inputs/text-input/text-input";

import { Locale } from "@/types/locale";

import styles from "./submit-form-page.module.scss";

const cx = classNames.bind(styles);

const ISSUE_OPTION_SUFFIXES = [
    "refundOrReturn",
    "rescheduling",
    "hardship",
    "loggingIn",
    "changingName",
    "verifyAccount",
    "complaint",
    "reportFraud",
    "somethingElse",
];

const ISSUE_OPTION_KEYS = ISSUE_OPTION_SUFFIXES.map((s) => `submitFormPage.issueOptions.${s}`);

const COUNTRY_OPTION_KEYS = [
    "submitFormPage.countryOptions.unitedStates",
    "submitFormPage.countryOptions.australia",
    "submitFormPage.countryOptions.newZealand",
    "submitFormPage.countryOptions.unitedKingdom",
    "submitFormPage.countryOptions.canada",
];

const COUNTRY_NAME_TO_CODE: Record<string, string> = {
    "United States": "US",
    Australia: "AU",
    "New Zealand": "NZ",
    "United Kingdom": "GB",
    Canada: "CA",
};

const LOCALE_TO_COUNTRY: Partial<Record<Locale, string>> = {
    "en-US": "United States",
    "en-AU": "Australia",
    "en-NZ": "New Zealand",
    "en-GB": "United Kingdom",
    "en-CA": "Canada",
    "fr-CA": "Canada",
};

const DESCRIPTION_MAX_LENGTH = 1000;

const useValidationSchema = () => {
    const { t } = useTranslation();
    return useMemo(
        () =>
            Yup.object({
                firstName: Yup.string().required(t("submitFormPage.validation.firstNameRequired")),
                lastName: Yup.string().required(t("submitFormPage.validation.lastNameRequired")),
                email: Yup.string()
                    .email(t("submitFormPage.validation.emailInvalid"))
                    .test(
                        "salesforce-compatible-email",
                        t("submitFormPage.validation.emailInvalid"),
                        (value) => !value || isValidEmailForSalesforce(value)
                    )
                    .required(t("submitFormPage.validation.emailRequired")),
                phone: Yup.string()
                    .matches(/^\d{7,15}$/, t("submitFormPage.validation.phoneInvalid"))
                    .optional(),
                country: Yup.string().required(t("submitFormPage.validation.countryRequired")),
                issue: Yup.string().required(t("submitFormPage.validation.issueRequired")),
                description: Yup.string()
                    .max(
                        DESCRIPTION_MAX_LENGTH,
                        t("submitFormPage.validation.descriptionMaxLength", {
                            max: DESCRIPTION_MAX_LENGTH,
                        })
                    )
                    .required(t("submitFormPage.validation.descriptionRequired")),
            }),
        [t]
    );
};

interface FormValues {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    country: string;
    issue: string;
    description: string;
}

const getInitialValues = (locale: Locale): FormValues => ({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: LOCALE_TO_COUNTRY[locale] ?? "United States",
    issue: "",
    description: "",
});

const toMenuItems = (options: string[]): DropdownMenuItem[] =>
    options.map((name, id) => ({ id, name }));

const useMenuItems = (options: string[]) => useMemo(() => toMenuItems(options), [options]);

interface FormSelectFieldProps {
    name: string;
    label: string;
    options: string[];
    storedValues?: string[];
    value: string;
    error?: string;
    touched?: boolean;
    setFieldValue: (field: string, value: string) => void;
    onBlurField: () => void;
}

const FormSelectField: React.FC<FormSelectFieldProps> = ({
    name,
    label,
    options,
    storedValues,
    value,
    error,
    touched,
    setFieldValue,
    onBlurField,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const isFilled = !!value;
    const showError = error && touched;
    const menuItems = useMenuItems(options);

    const displayValue = storedValues ? options[storedValues.indexOf(value)] ?? value : value;

    const triggerClassName = cx(styles.dropdownTrigger, {
        [styles.dropdownTriggerOpen]: isOpen,
        [styles.dropdownTriggerFilled]: isFilled,
        [styles.dropdownTriggerError]: showError,
    });

    const labelClassName = cx(styles.dropdownLabel, {
        [styles.dropdownLabelFilled]: isFilled,
        [styles.dropdownLabelError]: showError,
    });

    const menuClassName = cx(styles.dropdownMenuContainer, {
        [styles.dropdownMenuContainerShow]: isOpen,
    });

    const handleBlur: FocusEventHandler<HTMLDivElement> = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsOpen(false);
            onBlurField();
        }
    };

    const handleSelect = (item: DropdownMenuItem) => {
        const stored = storedValues ? storedValues[item.id] : item.name;
        setFieldValue(name, stored);
        setIsOpen(false);
    };

    return (
        <div className={styles.dropdownWrapper} onBlur={handleBlur}>
            <button
                type="button"
                className={triggerClassName}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className={labelClassName}>{label}</span>
                {isFilled && <span className={styles.dropdownValue}>{displayValue}</span>}
            </button>
            <div className={menuClassName}>
                <DropdownMenu
                    dropdownMenuItems={menuItems}
                    kind={DROPDOWN_KINDS.TEXT}
                    size={DROPDOWN_SIZES.REGULAR}
                    onClick={handleSelect}
                />
            </div>
            {showError && <p className={styles.errorText}>{error}</p>}
        </div>
    );
};

interface TextareaFieldProps {
    id: string;
    name: string;
    label: string;
    placeholder: string;
    value: string;
    maxLength: number;
    error?: string;
    touched?: boolean;
    onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
    onBlur: React.FocusEventHandler<HTMLTextAreaElement>;
}

const TextareaField: React.FC<TextareaFieldProps> = ({
    id,
    name,
    label,
    placeholder,
    value,
    maxLength,
    error,
    touched,
    onChange,
    onBlur,
}) => {
    const showError = error && touched;

    const labelWrapperClassName = cx(styles.labelWrapper, {
        [styles.labelWrapperError]: showError,
    });

    return (
        <div className={styles.fieldWrapper}>
            <div className={labelWrapperClassName}>
                <label htmlFor={id} className={styles.textareaLabel}>
                    {label}
                </label>
                <textarea
                    id={id}
                    name={name}
                    className={styles.textareaInput}
                    placeholder={placeholder}
                    value={value}
                    maxLength={maxLength}
                    onChange={onChange}
                    onBlur={onBlur}
                />
            </div>
            <div className={styles.charCount}>
                {value.length}/{maxLength}
            </div>
            {showError && <p className={styles.errorText}>{error}</p>}
        </div>
    );
};

const SubmitForm: React.FC<{ formikProps: FormikProps<FormikValues> }> = ({ formikProps }) => {
    const { t } = useTranslation();
    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
    } = formikProps;

    const countryOptions = useMemo(() => COUNTRY_OPTION_KEYS.map((key) => t(key)), [t]);
    const issueOptions = useMemo(() => ISSUE_OPTION_KEYS.map((key) => t(key)), [t]);

    return (
        <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.row}>
                <TextInput
                    type={TYPES.TEXT}
                    label={t("submitFormPage.form.firstName")}
                    name="firstName"
                    fullWidth
                    size={INPUT_SIZES.SMALL}
                    value={values.firstName as string}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched.firstName}
                    errors={errors.firstName as string}
                />
                <TextInput
                    type={TYPES.TEXT}
                    label={t("submitFormPage.form.lastName")}
                    name="lastName"
                    fullWidth
                    size={INPUT_SIZES.SMALL}
                    value={values.lastName as string}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched.lastName}
                    errors={errors.lastName as string}
                />
            </div>

            <div className={styles.row}>
                <TextInput
                    type={TYPES.EMAIL}
                    label={t("submitFormPage.form.email")}
                    name="email"
                    fullWidth
                    size={INPUT_SIZES.SMALL}
                    value={values.email as string}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched.email}
                    errors={errors.email as string}
                />
                <TextInput
                    type={TYPES.NUMBER}
                    label={t("submitFormPage.form.phone")}
                    name="phone"
                    fullWidth
                    size={INPUT_SIZES.SMALL}
                    value={values.phone as string}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched.phone}
                    errors={errors.phone as string}
                />
            </div>

            <FormSelectField
                name="country"
                label={t("submitFormPage.form.country")}
                value={values.country as string}
                options={countryOptions}
                error={errors.country as string}
                touched={touched.country as boolean}
                setFieldValue={setFieldValue}
                onBlurField={() => setFieldTouched("country", true)}
            />

            <FormSelectField
                name="issue"
                label={t("submitFormPage.form.issue")}
                value={values.issue as string}
                options={issueOptions}
                storedValues={ISSUE_OPTION_SUFFIXES}
                error={errors.issue as string}
                touched={touched.issue as boolean}
                setFieldValue={setFieldValue}
                onBlurField={() => setFieldTouched("issue", true)}
            />

            <TextareaField
                id="description"
                name="description"
                label={t("submitFormPage.form.descriptionLabel")}
                placeholder={t("submitFormPage.form.descriptionPlaceholder")}
                value={values.description as string}
                maxLength={DESCRIPTION_MAX_LENGTH}
                error={errors.description as string}
                touched={touched.description as boolean}
                onChange={handleChange}
                onBlur={handleBlur}
            />

            <div className={styles.submitButton}>
                <ActionButton
                    disabled={isSubmitting}
                    fluid
                    kind={BUTTON_KINDS.BLACK}
                    size={BUTTON_SIZES.REGULAR}
                    label={t("submitFormPage.form.submit")}
                    type="submit"
                    onClick={() => void 0}
                />
            </div>
        </form>
    );
};

export const SubmitFormPage: React.FC<{ locale: Locale }> = ({ locale }) => {
    const { t } = useTranslation();
    const validationSchema = useValidationSchema();
    const [submitResult, setSubmitResult] = useState<{
        success: boolean;
        message?: string;
    } | null>(null);

    const handleSubmit = async (values: FormikValues) => {
        const { country, ...rest } = values;
        const payload = {
            ...rest,
            country_code: COUNTRY_NAME_TO_CODE[country as string] ?? country,
        };
        try {
            const res = await fetch("/api/help-form/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            setSubmitResult({ success: data.success, message: data.message });
        } catch {
            setSubmitResult({ success: false });
        }
    };

    return (
        <div
            className={cx(styles.submitFormPage, {
                [styles.submitFormPageFull]: !submitResult?.success,
            })}
        >
            <div className={styles.card}>
                {submitResult?.success ? (
                    <div className={styles.successMessage}>
                        <h2>{t("submitFormPage.success.heading")}</h2>
                        <p>{t("submitFormPage.success.message")}</p>
                    </div>
                ) : (
                    <>
                        <h1 className={styles.title}>{t("submitFormPage.title")}</h1>
                        <p className={styles.intro}>{t("submitFormPage.intro")}</p>
                        {submitResult && !submitResult.success && (
                            <p className={styles.errorText}>
                                {submitResult.message || t("submitFormPage.error.genericError")}
                            </p>
                        )}
                        <Formik
                            initialValues={getInitialValues(locale)}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {(formikProps) => <SubmitForm formikProps={formikProps} />}
                        </Formik>
                        <p className={styles.privacyNotice}>{t("submitFormPage.privacyNotice")}</p>
                    </>
                )}
            </div>
        </div>
    );
};
